// src/components/layout/Sidebar.tsx
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { Button } from '../ui/Button';
import { navigationMenuService, NavigationMenuItem } from '../../services/navigationMenuService';
import { useAuth } from '../../contexts/AuthContext';
import { ChevronsLeft, ChevronsRight, EuroIcon, LayoutGrid, ListChecks, MessageCircle, MessageSquare, Smartphone, Workflow, X, UserIcon, Globe, KeyRound, Bot } from 'lucide-react';

type SidebarProps = {
  sidebarState: 'open' | 'narrow' | 'closed';
  setSidebarViewClosed: () => void;
  onToggleDesktop: () => void;
  isMobile: boolean;
};

export default function Sidebar({ sidebarState, setSidebarViewClosed, onToggleDesktop, isMobile }: SidebarProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [navigationItems, setNavigationItems] = useState<NavigationMenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchMenus = async () => {
      try {
        // Pass userId to get personalized menu permissions
        const menus = await navigationMenuService.getActive(user?.id);

        if(user?.customData?.admin) {
          const adminMenus: NavigationMenuItem[] = [
            {
              id: 9000,
              translationKey: 'userManagement',
              href: '/users',
              icon: 'UserIcon',
              exact: false,
              order: 900,
              active: true,
              isSystem: true,
              isAdmin: true,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
            {
              id: 9001,
              translationKey: 'plansManagement',
              href: '/subscriptions-manage',
              icon: 'EuroIcon',
              exact: false,
              order: 901,
              active: true,
              isSystem: true,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
            {
              id: 9002,
              translationKey: 'bitrixAllowedDomains',
              href: '/bitrix-allowed-domains',
              icon: 'Globe',
              exact: false,
              order: 902,
              active: true,
              isSystem: true,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
            {
              id: 9003,
              translationKey: 'channelPlansStatusMenu',
              href: '/channel-plans-status',
              icon: 'ListChecks',
              exact: false,
              order: 903,
              active: true,
              isSystem: true,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
            {
              id: 9004,
              translationKey: 'agentSettingsMenu',
              href: '/agent-settings',
              icon: 'KeyRound',
              exact: false,
              order: 904,
              active: true,
              isSystem: true,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
            {
              id: 9005,
              translationKey: 'chatbotSettingsMenu',
              href: '/chatbot-settings',
              icon: 'Bot',
              exact: false,
              order: 905,
              active: true,
              isSystem: true,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            }
          ]

          menus.push(...adminMenus);
        }


        setNavigationItems(menus);
      } catch (error) {
        console.error('Error loading navigation menus:', error);
        // Fallback to default menu if API fails
        setNavigationItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMenus();
  }, [user]);

  const isOpen = sidebarState === 'open';
  const isNarrow = sidebarState === 'narrow';

  let sidebarWidthClass = 'w-64';
  if (isNarrow && !isMobile) {
    sidebarWidthClass = 'w-20';
  }

  // Helper to get icon component dynamically
  const getIconComponent = (iconName: string) => {
        let Icon = LayoutGrid;

        switch (iconName) {
            case "MessageSquare":
                Icon = MessageSquare;
                break;
            case "Workflow":
                Icon = Workflow;
                break;
            case "MessageCircle":
                Icon = MessageCircle;
                break;
            case "Smartphone":
                Icon = Smartphone;
                break;
            case "EuroIcon":
                Icon = EuroIcon;
                break;
            case "UserIcon":
                Icon = UserIcon;
                break;
            case "Globe":
                Icon = Globe;
                break;
            case "ListChecks":
                Icon = ListChecks;
                break;
            case "KeyRound":
                Icon = KeyRound;
                break;
            case "Bot":
                Icon = Bot;
                break;
        }
      return Icon;
  };

  if (loading) {
    return null; // Or a loading skeleton
  }

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/30 backdrop-blur-sm md:hidden"
          onClick={setSidebarViewClosed}
          aria-hidden="true"
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex flex-col border-r border-gray-200 bg-gray-50 
                  transition-all duration-300 ease-in-out
                  ${sidebarWidthClass} 
                  ${isMobile && sidebarState === 'closed' ? '-translate-x-full' : 'translate-x-0'}`}
      >
        <div className={`flex h-16 items-center border-b border-gray-200 px-4 ${isNarrow && !isMobile ? 'justify-center' : 'justify-between'}`}>
          {(!isNarrow || isMobile) && <span className="text-lg font-semibold text-gray-800"><span className=' text-blue-600 text-2xl'>24</span>Messaging</span>}
          {isMobile && sidebarState === 'open' && (
             <Button variant="ghost" size="icon" onClick={setSidebarViewClosed}>
               <X className="h-6 w-6 text-gray-600" />
               <span className="sr-only">{t('closeMenu')}</span>
             </Button>
          )}
        </div>
        <nav className="flex-1 space-y-1 p-2">
          {navigationItems.map((item) => {
            const IconComponent = getIconComponent(item.icon);
            return (
              <>
                {item.isAdmin && (
                  <>
                    <div className='flex my-4'>
                      
                      <div className={`w-full border-t border-gray-300 my-2 ${!isOpen && " hidden"}`}></div>
                      <div className=' text-xs text-gray-500 text-center w-full ' >Admin</div>
                      <div className={`w-full border-t border-gray-300 my-2 ${!isOpen && " hidden"}`}></div>
                    </div>
                  </>
                )}

                <NavLink
                  key={item.id}
                  to={item.href}
                  onClick={() => { if (isMobile) setSidebarViewClosed(); }}
                  className={({ isActive }) =>
                    `flex items-center rounded-md px-3 py-2.5 text-sm font-medium ${isNarrow && !isMobile ? 'justify-center' : ''} ${
                      isActive
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'text-gray-600 hover:bg-gray-200 hover:text-gray-800'
                    }

                    
                    
                    `
                  }
                  title={isNarrow && !isMobile ? t(item.translationKey) : undefined}
                  end={item.exact}
                >
                  <IconComponent className={`h-5 w-5 flex-shrink-0 ${(isOpen || isMobile) && !isNarrow ? 'mr-3' : 'mr-0'}`} />
                  {(isOpen || isMobile) && !isNarrow && <span>{t(item.translationKey)}</span>}
                </NavLink>
              </>
            );
          })}
        </nav>
        {!isMobile && (
          <div className="mt-auto border-t border-gray-200 p-2">
            <Button variant="ghost" onClick={onToggleDesktop} className="w-full justify-center text-gray-600 hover:text-gray-800">
              {isOpen ? <ChevronsLeft className="h-5 w-5" /> : <ChevronsRight className="h-5 w-5" />}
              <span className="sr-only">{isOpen ? t('collapseSidebar') : t('expandSidebar')}</span>
            </Button>
          </div>
        )}
      </aside>
    </>
  );
}
